import type {
  ChangeEvent as ReactChangeEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from "react";
import { action, computed, makeObservable } from "mobx";
import { injectable } from "tsyringe";
import { ViewModel } from "../../lib/vvm";
import { parseTypeTagsInput } from "../../utils/typeTags";
import type { InputOnChangeData } from "../Input/Input";
import type { TypeTagsInputProps } from "./TypeTagsInput";

const COMMIT_KEYS = new Set(["Enter", "Tab", ",", ";"]);

@injectable()
export class TypeTagsInputViewModel extends ViewModel<
  unknown,
  TypeTagsInputProps
> {
  constructor() {
    super();
    makeObservable(this);
  }

  @computed
  get tags(): string[] {
    return this.viewProps.tags;
  }

  @computed
  get suggestions(): string[] {
    return this.viewProps.suggestions;
  }

  @computed
  get inputValue(): string {
    return this.viewProps.inputValue;
  }

  @computed
  get placeholder(): string {
    return this.viewProps.placeholder || "Например: #еда #молочка";
  }

  @computed
  get disabled(): boolean {
    return this.viewProps.disabled ?? false;
  }

  @computed
  get datalistId(): string {
    return this.viewProps.datalistId;
  }

  @computed
  get filteredSuggestions(): string[] {
    const query = this.inputValue
      .trim()
      .replace(/^#+/g, "")
      .toLocaleLowerCase("ru-RU");

    return this.suggestions
      .filter((suggestion) => !this.tags.includes(suggestion))
      .filter((suggestion) =>
        !query ? true : suggestion.toLocaleLowerCase("ru-RU").includes(query),
      )
      .slice(0, 8);
  }

  @action.bound
  onInputChange(
    _event: ReactChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void {
    this.viewProps.onInputValueChange(data.value);
  }

  @action.bound
  onInputKeyDown(event: ReactKeyboardEvent<HTMLInputElement>): void {
    if (!COMMIT_KEYS.has(event.key)) {
      return;
    }

    event.preventDefault();
    this.commitInput();
  }

  @action.bound
  onAddButtonClick(): void {
    this.commitInput();
  }

  @action.bound
  onTagRemove(tag: string): void {
    this.viewProps.onRemoveTag(tag);
  }

  @action.bound
  onSuggestionMouseDown(
    event: ReactMouseEvent<HTMLButtonElement>,
    suggestion: string,
  ): void {
    event.preventDefault();

    if (this.disabled) {
      return;
    }

    this.viewProps.onAddTags([suggestion]);
    this.viewProps.onInputValueChange("");
  }

  @action.bound
  commitInput(): void {
    if (this.disabled) {
      return;
    }

    const parsed = parseTypeTagsInput(this.inputValue);
    if (parsed.length === 0) {
      return;
    }

    this.viewProps.onAddTags(parsed);
    this.viewProps.onInputValueChange("");
  }
}
